import { Box, Flex, SimpleGrid, VStack } from '@chakra-ui/react';
import { Button, Icons, SvgSpriteConsumer } from '@pluxee-design-system/core';
import { CheckboxControl, InputControl, SubmitButton } from '@pluxee-design-system/react-hook-form';
import { useCompanyPositions } from 'api/companyApi';
import VideoButton from 'common/buttons/VideoButton';
import { PhoneNumberControl, SelectControl } from 'common/forms';
import { SplitScreen } from 'common/layouts';
import { TextTitle } from 'common/typography';
import CampaignLogo from 'components/Affiliation/CampaignLogo';
import { PositionSelect } from 'components/Employees/misc';
import { mapGroupToKey } from 'components/Form';
import { WizardStep } from 'components/WizardForm/types';
import { createSchema } from 'config';
import useCurrentLocale from 'data/useCurrentLocale';
import useCurrentUser from 'data/useCurrentUser';
import useSession from 'data/useSession';
import useTranslations from 'i18n';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import gtm, { gtmUtils } from 'trackers/gtm';
import { PageVideoEnum } from 'types/videos';
import scrollToTop from 'utils/scrollToTop';
import Stepper from '../Stepper';
import { AffiliationValues, ExtraWizardProps } from '../types';

interface Language {
  key: string;
  name: string;
}

const selectLanguage = (languages: Language[], locale?: string) =>
  (languages.find((l) => locale?.toLowerCase()?.includes(l.key.toLowerCase())) || languages[0])
    ?.key || '';

const trackSubmit = () => {
  gtm.trackClickContactConfirm(
    gtmUtils.getSelectedOptions('ContactInfoStep_position'),
    gtmUtils.getSelectedOptions('ContactInfoStep_language'),
  );
  scrollToTop();
};
const trackBack = () =>
  gtm.trackClickContactBack(
    gtmUtils.getSelectedOptions('ContactInfoStep_position'),
    gtmUtils.getSelectedOptions('ContactInfoStep_language'),
  );

export const ContactInfoStep: WizardStep<ExtraWizardProps, AffiliationValues> = ({
  activeStep,
  campaign,
  config: {
    contactInfo: { form: config },
  },
  toPrev: toPrevRaw,
  toStep,
}) => {
  const [session] = useSession();
  const currentUser = session!.user!;
  const { value: currentLocale } = useCurrentLocale();
  const { isBOUser } = useCurrentUser();
  const { t, tGroup } = useTranslations();
  const { setValue, watch } = useFormContext<AffiliationValues>();
  const isDisabled = isBOUser ? false : !config.signatoryPerson.visible;
  const { data: positions } = useCompanyPositions();
  const toPrev = useCallback(() => {
    trackBack();
    toPrevRaw();
  }, [toPrevRaw]);

  const valuesLanguage = watch('language');
  const valuesLanguageSet = watch('languageSet');
  const valuesPosition = watch('position');
  const valuesPowerOfAttorneyRequired = watch('powerOfAttorneyRequired');
  const valuesSignatoryPerson = watch('signatoryPerson');
  const languages = useMemo(() => mapGroupToKey(tGroup('languages.languages')), [tGroup]);

  useEffect(() => {
    if (valuesSignatoryPerson) {
      setValue('firstName', currentUser.firstName);
      setValue('lastName', currentUser.lastName);
      setValue('email', currentUser.email);
      setValue('phoneNumber', currentUser.telephone);
    }
  }, [valuesSignatoryPerson, setValue, currentUser]);

  useEffect(() => {
    if (!valuesLanguageSet) {
      setValue('language', selectLanguage(languages, currentLocale));
      setValue('languageSet', true);
    }
    setValue('contactLanguage', selectLanguage(languages, currentUser?.locale));
    // change power of attorney requirement
    setValue(
      'powerOfAttorneyRequired',
      positions?.entries?.some((p) => p.id === Number(valuesPosition) && p.poaRequired) ?? false,
    );
    setValue('contactPosition', positions?.entries?.find((p) => p.defaultOption)?.id ?? null);
  }, [
    currentUser?.locale,
    currentLocale,
    languages,
    positions?.entries,
    valuesLanguage,
    valuesLanguageSet,
    valuesPosition,
    setValue,
  ]);

  return (
    <SplitScreen>
      <Flex gap={10} direction="column">
        <Box>
          {campaign && <CampaignLogo campaign={campaign} />}
          <VideoButton page={PageVideoEnum.AffiliationContactInfo} />
          <Stepper activeStep={activeStep} onStepChange={toStep} />
          <TextTitle
            mt={6}
            title={t('contact_info.pageHeader', 'Who is going to be our new partner')}
            subtitle={t(
              'contact_info.subheader',
              'If you are not the person who will be in charge of checking the monthly activity',
            )}
          />
        </Box>
        <VStack align="stretch" spacing={4}>
          {config.signatoryPerson.visible && (
            <CheckboxControl
              mandatory={config.signatoryPerson.mandatory}
              name="signatoryPerson"
              id="ContactInfoStep_signatoryPerson"
              disabled={isBOUser}
            >
              {t(
                'contact_info.form.signatoryPerson.label',
                'Signatory person (Prefill your information)',
              )}
            </CheckboxControl>
          )}
          {(config.lastName.visible || config.firstName.visible) && (
            <SimpleGrid
              columns={{ base: 1, md: config.lastName.visible && config.firstName.visible ? 2 : 1 }}
              spacing={4}
            >
              {config.firstName.visible && (
                <InputControl
                  type="text"
                  mandatory={config.firstName.mandatory}
                  name="firstName"
                  id="ContactInfoStep_firstName"
                  placeholder={t('contact_info.form.firstName.label', 'First name')}
                  title={t('contact_info.form.firstName.label', 'First name')}
                  disabled={isDisabled || valuesSignatoryPerson}
                />
              )}
              {config.lastName.visible && (
                <InputControl
                  type="text"
                  mandatory={config.lastName.mandatory}
                  name="lastName"
                  id="ContactInfoStep_lastName"
                  placeholder={t('contact_info.form.lastName.label', 'Last name')}
                  title={t('contact_info.form.lastName.label', 'Last name')}
                  disabled={isDisabled || valuesSignatoryPerson}
                />
              )}
            </SimpleGrid>
          )}
          {config.email.visible && (
            <InputControl
              type="email"
              mandatory={config.email.mandatory}
              name="email"
              id="ContactInfoStep_email"
              placeholder={t('contact_info.form.email.label', 'Email')}
              title={t('contact_info.form.email.label', 'Email')}
              disabled={isDisabled || valuesSignatoryPerson}
            />
          )}
          {config.phoneNumber.visible && (
            <PhoneNumberControl
              mandatory={config.phoneNumber.mandatory}
              name="phoneNumber"
              id="ContactInfoStep_phoneNumber"
              title={t('contact_info.form.phoneNumber.label', 'Phone number')}
              disabled={isDisabled || valuesSignatoryPerson}
            />
          )}
          {config.language.visible && (
            <SelectControl
              mandatory={config.language.mandatory}
              name="language"
              id="ContactInfoStep_language"
              notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
              placeholder={t('contact_info.form.language.placeholder', 'Language')}
              title={t('contact_info.form.language.label', 'Language')}
              options={languages}
            />
          )}
          {config.position.visible && (
            <PositionSelect
              mandatory={config.position.mandatory}
              name="position"
              id="ContactInfoStep_position"
              placeholder={t('contact_info.form.position.placeholder', 'Select your position')}
              title={t('contact_info.form.position.label', 'Position')}
              notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
              {...(valuesPowerOfAttorneyRequired
                ? {
                    helpText: t(
                      'contact_info.sectionTexts.positionHelpText',
                      'In case you are not the person authorized to act on behalf of this company, you will be required to upload a power of attorney in next step.',
                    ),
                    helpTextIcon: <SvgSpriteConsumer svgId={Icons.INFO_OUTLINE16} size={16} />,
                  }
                : {})}
            />
          )}
        </VStack>
        <VStack align="stretch" spacing={4}>
          <SubmitButton
            variant="primaryFilled"
            id="ContactInfoStep__validateButton"
            onClick={trackSubmit}
            width="100%"
          >
            {t('global_texts.buttons.confirm', 'Confirm')}
          </SubmitButton>
          <Button
            variant="primaryOutlined"
            id="CompanyInfoStep__backButton"
            type="button"
            onClick={toPrev}
            width="100%"
            background="semantic.surface.1"
          >
            {t('global_texts.buttons.back', 'Back')}
          </Button>
        </VStack>
      </Flex>
    </SplitScreen>
  );
};

ContactInfoStep.initial = () => ({
  firstName: '',
  lastName: '',
  position: null,
  contactPosition: null,
  email: '',
  phoneNumber: '',
  language: '',
  languageSet: false,
  contactLanguage: '',
  signatoryPerson: false,
});

ContactInfoStep.schema = ({ config }) => createSchema(config.contactInfo.form);

export default ContactInfoStep;
